import { useState } from "react";
import axios from "axios";
import { Minus, Plus, Trash2, Send, Check, Download } from "lucide-react";import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { useCart } from "../context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["Hotel", "Hospital", "Medical College", "School", "Other Institution"];

const emptyForm = { institution_name: "", contact_person: "", phone: "", email: "", institution_type: "Hotel", message: "" };

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, remove, clear } = useCart();
  const [form, setForm] = useState(emptyForm);
  const [sending, setSending] = useState(false);
  const [successRef, setSuccessRef] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const parsePrice = (s) => {
    const m = (s || "").replace(/,/g, "").match(/(\d+(?:\.\d+)?)/);
    return m ? parseFloat(m[1]) : null;
  };
  const estTotal = items.reduce((sum, i) => {
    const p = parsePrice(i.price);
    return p !== null ? sum + p * i.qty : sum;
  }, 0);
  const hasPriced = items.some((i) => parsePrice(i.price) !== null);

  const submit = async (e) => {
    e.preventDefault();
    if (!items.length) {
      toast.error("Add at least one product to your quote list");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/quotes`, { ...form, items });
      setSuccessRef(data.reference);
      toast.success(`Quote request ${data.reference} received. Our team will call you back.`);
      clear();
      setForm(emptyForm);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-[#FAF8FC] border border-[#221533]/15 rounded-md px-4 py-3.5 text-sm text-[#221533] placeholder-[#221533]/40 focus:border-[#8E5A9E] transition-colors duration-300";

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        data-testid="cart-drawer"
        side="right"
        className="w-full sm:max-w-md bg-white border-l border-[#221533]/10 text-[#221533] overflow-y-auto p-0"
      >
        <SheetHeader className="p-6 border-b border-[#221533]/10 text-left">
          <SheetTitle className="font-clash text-2xl text-[#221533]">Bulk Quote Request</SheetTitle>
          <SheetDescription className="font-mono2 text-[10px] tracking-[0.25em] uppercase text-[#221533]/40">
            Direct from factory · ZALTA®
          </SheetDescription>
        </SheetHeader>

        {successRef ? (
          <div data-testid="quote-success" className="p-8 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#8E5A9E]/10 flex items-center justify-center mb-5">
              <Check size={26} className="text-[#8E5A9E]" />
            </div>
            <p className="font-clash text-2xl font-semibold text-[#221533]">Quote request received</p>
            <p className="font-mono2 text-sm tracking-[0.15em] text-[#74427F] mt-3">{successRef}</p>
            <p className="text-sm text-[#221533]/55 font-light mt-3">Our team will call you back shortly. Keep your copy:</p>
            <a
              data-testid="quote-pdf-download"
              href={`${API}/quotes/${successRef}/pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 bg-[#8E5A9E] hover:bg-[#74427F] text-white font-mono2 text-xs tracking-[0.2em] uppercase px-6 py-3.5 rounded-md transition-colors active:scale-95"
            >
              <Download size={14} /> Download PDF Copy
            </a>
            <button
              data-testid="quote-done-btn"
              onClick={() => { setSuccessRef(""); setOpen(false); }}
              className="block mx-auto mt-4 text-xs text-[#221533]/45 hover:text-[#74427F] transition-colors"
            >
              Done — back to catalogue
            </button>
          </div>
        ) : (
        <>
        <div className="p-6 space-y-4" data-testid="cart-items-list">
          {items.length === 0 && (
            <p className="text-sm text-[#221533]/45 py-6 text-center font-light">
              Your quote list is empty — add products from the catalogue.
            </p>
          )}
          {items.map((i) => (
            <div key={i.key} data-testid={`cart-item-${i.key}`} className="flex items-center gap-4 border border-[#221533]/10 rounded-lg p-4 bg-[#FAF8FC]">
              <div className="flex-1 min-w-0">
                <p className="font-clash text-base truncate">{i.name}</p>
                <p className="font-mono2 text-[10px] tracking-[0.15em] text-[#221533]/40 uppercase">{i.pack_size}</p>
                {i.price && (
                  <p data-testid={`cart-price-${i.key}`} className="text-xs font-bold text-[#74427F] mt-0.5">{i.price}</p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button data-testid={`qty-minus-${i.key}`} onClick={() => updateQty(i.key, -1)} className="w-8 h-8 border border-[#221533]/20 rounded-md flex items-center justify-center hover:border-[#8E5A9E] hover:text-[#74427F] active:scale-90 transition-colors">
                  <Minus size={12} />
                </button>
                <span className="font-mono2 text-sm w-6 text-center" data-testid={`qty-${i.key}`}>{i.qty}</span>
                <button data-testid={`qty-plus-${i.key}`} onClick={() => updateQty(i.key, 1)} className="w-8 h-8 border border-[#221533]/20 rounded-md flex items-center justify-center hover:border-[#8E5A9E] hover:text-[#74427F] active:scale-90 transition-colors">
                  <Plus size={12} />
                </button>
                <button data-testid={`remove-${i.key}`} onClick={() => remove(i.key)} className="w-8 h-8 flex items-center justify-center text-[#221533]/40 hover:text-[#E63946] transition-colors">
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
          {hasPriced && items.length > 0 && (
            <div data-testid="cart-estimated-total" className="flex items-center justify-between bg-[#8E5A9E] text-white rounded-lg px-4 py-3">
              <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase">Estimated Total</span>
              <span className="font-clash text-lg font-semibold">₹{estTotal.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
            </div>
          )}
        </div>

        <form onSubmit={submit} className="p-6 border-t border-[#221533]/10 space-y-3" data-testid="quote-form">
          <p className="font-mono2 text-[10px] tracking-[0.3em] uppercase text-[#8E5A9E] pb-2">Institution Details</p>
          <input data-testid="quote-institution-name" required placeholder="Institution / Hotel name *" value={form.institution_name} onChange={set("institution_name")} className={inputCls} />
          <input data-testid="quote-contact-person" required placeholder="Contact person *" value={form.contact_person} onChange={set("contact_person")} className={inputCls} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="quote-phone" required placeholder="Phone *" value={form.phone} onChange={set("phone")} className={inputCls} />
            <input data-testid="quote-email" required type="email" placeholder="Email *" value={form.email} onChange={set("email")} className={inputCls} />
          </div>
          <select data-testid="quote-institution-type" value={form.institution_type} onChange={set("institution_type")} className={`${inputCls} appearance-none`}>
            {TYPES.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
          <textarea data-testid="quote-message" rows={3} placeholder="Requirement, delivery city, timelines…" value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} />
          <button
            data-testid="quote-submit-btn"
            type="submit"
            disabled={sending}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#8E5A9E] hover:bg-[#74427F] disabled:opacity-50 text-white font-mono2 text-xs tracking-[0.25em] uppercase px-6 py-4 rounded-md transition-colors duration-300 active:scale-[0.98]"
          >
            <Send size={14} /> {sending ? "Sending…" : "Request Bulk Quote"}
          </button>
        </form>
        </>
        )}
      </SheetContent>
    </Sheet>
  );
};
